import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  ArrowUp, ArrowDown, Check, X, MusicNote, FloppyDisk, FilmStrip,
  Scissors, TextAa, Sparkle, CheckCircle, Upload, SpeakerHigh,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { assetUrl } from "../../lib/assetUrl";

/* Drag-drop scene editor + per-clip trim + text overlay + music upload.
   Server-side render — admin saves edits, then clicks "Render final"
   which triggers a one-shot ffmpeg pass that applies everything. */

const POSITIONS = [
  { id: "top",    label: "Top" },
  { id: "middle", label: "Center" },
  { id: "bottom", label: "Bottom" },
];

export default function TimelineEditor({ draft, scenes, draftId, reload }) {
  const [edits, setEdits] = useState([]);
  const [music, setMusic] = useState({ file_id: null, volume: 0.25 });
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileInputRef = useRef(null);
  const [castList, setCastList] = useState([]);

  // Voiceover status — Phase 2.B. Polled when the editor opens; refreshed
  // after Generate so the count badge stays accurate.
  const [voStatus, setVoStatus] = useState(null);
  const refreshVoStatus = useCallback(() => {
    api.get(`/admin/social/drafts/${draftId}/voiceover-status`)
      .then(({ data }) => setVoStatus(data))
      .catch(() => setVoStatus(null));
  }, [draftId]);
  useEffect(() => { refreshVoStatus(); }, [refreshVoStatus]);

  // Phase 2.C — load cast list so we can show per-scene voice picker. Uses
  // the Phase 2.D /cast-v2 endpoint which adds HeyGen bindings (avatar/voice).
  useEffect(() => {
    api.get("/admin/social/cast-v2")
      .then(({ data }) => setCastList(data || []))
      .catch(() =>
        api.get("/admin/social/cast")
          .then(({ data }) => setCastList(data || []))
          .catch(() => setCastList([]))
      );
  }, []);

  // Initialize edit state from saved timeline_edits (if any) or from scenes order.
  useEffect(() => {
    const saved = draft.timeline_edits || [];
    if (saved.length) {
      // Reconcile saved edits with current scene list (drop deleted, add new).
      const byId = new Map(saved.map((e) => [e.scene_id, e]));
      const reconciled = scenes.map((s, i) => byId.get(s.id) || {
        scene_id: s.id, include: true, order: i,
        trim_start_ms: 0, trim_end_ms: null,
        text_overlay: { text: "", position: "bottom" },
        voice_id: "",
      });
      reconciled.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setEdits(reconciled);
    } else {
      setEdits(scenes.map((s, i) => ({
        scene_id: s.id, include: true, order: i,
        trim_start_ms: 0, trim_end_ms: null,
        text_overlay: { text: "", position: "bottom" },
        voice_id: "",
      })));
    }
    setMusic({
      file_id: draft.background_music_file_id || null,
      volume: typeof draft.music_volume === "number" ? draft.music_volume : 0.25,
    });
  }, [draft, scenes]);

  const updateEdit = (idx, patch) => {
    setEdits((es) => es.map((e, i) => i === idx ? { ...e, ...patch } : e));
  };
  const move = (from, to) => {
    if (to < 0 || to >= edits.length) return;
    setEdits((es) => {
      const copy = [...es];
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy.map((e, i) => ({ ...e, order: i }));
    });
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    move(dragIdx, i);
    setDragIdx(null);
  };

  const saveEdits = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/social/drafts/${draftId}/timeline-edits`, {
        scenes: edits.map((e, i) => ({ ...e, order: i })),
        background_music_file_id: music.file_id,
        music_volume: music.volume,
      });
      toast.success("Timeline edits saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const renderFinal = async () => {
    if (!window.confirm("Render the final video? This runs ffmpeg server-side and may take 30-90 seconds.")) return;
    setBusy(true);
    try {
      await api.put(`/admin/social/drafts/${draftId}/timeline-edits`, {
        scenes: edits.map((e, i) => ({ ...e, order: i })),
        background_music_file_id: music.file_id,
        music_volume: music.volume,
      });
      // Phase 2.B — v2 endpoint handles voiceover muxing when enabled.
      const useV2 = voStatus?.voiceover_enabled && voStatus?.scenes_with_voiceover > 0;
      const path = useV2 ? "render-timeline-v2" : "render-timeline";
      const { data } = await api.post(`/admin/social/drafts/${draftId}/${path}`);
      toast.success(
        `Rendered ${data.scenes_rendered} scenes · music ${data.music_mixed ? "mixed" : "skipped"}${data.voiceover_muxed ? " · voiceover on" : ""}`
      );
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const generateVoiceover = async () => {
    if (!voStatus?.elevenlabs_key_configured) {
      toast.error("Paste an ElevenLabs API key under Storyboard → Provider keys first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/social/drafts/${draftId}/voiceover`);
      if (data.generated > 0) {
        toast.success(`Generated voiceover for ${data.generated} scene${data.generated === 1 ? "" : "s"} · voice: ${data.voice_label}`);
      } else {
        toast.error(`No voiceover produced. First error: ${data.failed?.[0]?.error || "unknown"}`);
      }
      refreshVoStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const clearVoiceover = async () => {
    try {
      await api.delete(`/admin/social/drafts/${draftId}/voiceover`);
      toast.success("Voiceover disabled for this draft.");
      refreshVoStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const approve = async () => {
    try {
      await api.post(`/admin/social/drafts/${draftId}/approve`, { approved: !draft.approved });
      toast.success(draft.approved ? "Approval removed" : "Draft approved — 7am cron will publish it");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const uploadMusic = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", file);
      const { data } = await api.post(`/admin/social/drafts/${draftId}/music`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMusic((m) => ({ ...m, file_id: data.file_id }));
      toast.success("Music uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const includedCount = edits.filter((e) => e.include).length;

  return (
    <div className="card-flat p-5 space-y-4" data-testid="timeline-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <FilmStrip size={14} weight="duotone" /> Timeline editor
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 max-w-2xl">
            Drag rows to reorder, untick to exclude. Trim each clip's start/end in milliseconds. Add
            per-clip text overlay. Drop a background-music file to mix at the end. Hit{" "}
            <b>Render final</b> when ready — server runs one ffmpeg pass and replaces the draft video.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={approve}
            className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 ${draft.approved ? "bg-[#5FB37C] text-white" : "btn-ghost"}`}
            data-testid="timeline-approve"
          >
            {draft.approved
              ? (<><CheckCircle size={12} weight="fill" /> Approved · 7am IST will publish</>)
              : (<>Approve for 7am publish</>)}
          </button>
        </div>
      </div>

      <div className="space-y-2" data-testid="timeline-rows">
        {edits.map((e, i) => {
          const scene = scenes.find((s) => s.id === e.scene_id);
          if (!scene) return null;
          return (
            <div
              key={e.scene_id}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(i)}
              className={`card-flat p-3 flex items-start gap-3 ${e.include ? "" : "opacity-40"} ${dragIdx === i ? "border-l-2 border-[hsl(var(--primary))]" : ""}`}
              data-testid={`timeline-row-${e.scene_id}`}
            >
              {/* Reorder controls */}
              <div className="flex flex-col gap-1 pt-1">
                <button
                  onClick={() => move(i, i - 1)}
                  disabled={i === 0}
                  className="opacity-50 hover:opacity-100 disabled:opacity-20"
                  data-testid={`timeline-up-${e.scene_id}`}
                  aria-label="Move up"
                >
                  <ArrowUp size={14} weight="bold" />
                </button>
                <button
                  onClick={() => move(i, i + 1)}
                  disabled={i === edits.length - 1}
                  className="opacity-50 hover:opacity-100 disabled:opacity-20"
                  data-testid={`timeline-down-${e.scene_id}`}
                  aria-label="Move down"
                >
                  <ArrowDown size={14} weight="bold" />
                </button>
              </div>

              {/* Scene meta + thumbnail */}
              <div className="flex items-center gap-3 w-44 shrink-0">
                {scene.video_url ? (
                  <video src={assetUrl(scene.video_url)} className="w-24 h-14 object-cover border hairline" muted />
                ) : (
                  <div className="w-24 h-14 bg-muted border hairline flex items-center justify-center text-[10px] text-muted-foreground">
                    no video
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-[10px] text-muted-foreground">Scene {scene.index}</div>
                  <div className="text-xs font-medium truncate">{scene.title}</div>
                </div>
              </div>

              {/* Include toggle */}
              <label
                className="inline-flex items-center gap-1 text-xs cursor-pointer"
                data-testid={`timeline-include-${e.scene_id}`}
              >
                <input
                  type="checkbox"
                  checked={e.include}
                  onChange={(ev) => updateEdit(i, { include: ev.target.checked })}
                />
                Include
              </label>

              {/* Trim */}
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid={`timeline-trim-${e.scene_id}`}>
                <Scissors size={12} weight="duotone" />
                <label className="inline-flex items-center gap-1">
                  Start
                  <input
                    type="number" min={0} step={100}
                    value={e.trim_start_ms}
                    onChange={(ev) => updateEdit(i, { trim_start_ms: parseInt(ev.target.value, 10) || 0 })}
                    className="bg-background border hairline px-1.5 py-0.5 text-[11px] w-16"
                    data-testid={`timeline-trim-start-${e.scene_id}`}
                  />
                  ms
                </label>
                <label className="inline-flex items-center gap-1">
                  End
                  <input
                    type="number" min={0} step={100}
                    value={e.trim_end_ms ?? ""}
                    onChange={(ev) => updateEdit(i, {
                      trim_end_ms: ev.target.value === "" ? null : parseInt(ev.target.value, 10),
                    })}
                    placeholder="auto"
                    className="bg-background border hairline px-1.5 py-0.5 text-[11px] w-20"
                    data-testid={`timeline-trim-end-${e.scene_id}`}
                  />
                  ms
                </label>
              </div>

              {/* Text overlay */}
              <div className="flex-1 flex items-center gap-2 text-[11px] text-muted-foreground min-w-0" data-testid={`timeline-text-${e.scene_id}`}>
                <TextAa size={12} weight="duotone" />
                <input
                  type="text"
                  value={e.text_overlay?.text ?? ""}
                  onChange={(ev) => updateEdit(i, {
                    text_overlay: { ...(e.text_overlay || {}), text: ev.target.value },
                  })}
                  placeholder="Text overlay (optional)"
                  className="bg-background border hairline px-2 py-1 text-[11px] flex-1 min-w-0"
                  data-testid={`timeline-text-input-${e.scene_id}`}
                />
                <select
                  value={e.text_overlay?.position ?? "bottom"}
                  onChange={(ev) => updateEdit(i, {
                    text_overlay: { ...(e.text_overlay || {}), position: ev.target.value },
                  })}
                  className="bg-background border hairline px-1.5 py-1 text-[11px]"
                  data-testid={`timeline-text-pos-${e.scene_id}`}
                >
                  {POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>

              {/* Per-scene voice picker — Phase 2.C. Falls back to first cast
                  member's voice_id when set to "Default". Lets a different
                  character speak each scene. */}
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`timeline-voice-${e.scene_id}`}>
                <SpeakerHigh size={11} weight="duotone" />
                <select
                  value={e.voice_id || ""}
                  onChange={(ev) => updateEdit(i, { voice_id: ev.target.value })}
                  className="bg-background border hairline px-1.5 py-1 text-[11px] max-w-[120px]"
                  data-testid={`timeline-voice-pick-${e.scene_id}`}
                  title="Voice for this scene"
                >
                  <option value="">Default</option>
                  {castList.filter((c) => c.voice_id).map((c) => (
                    <option key={c.id} value={c.voice_id}>
                      {c.name}{c.voice_label ? ` · ${c.voice_label.slice(0, 16)}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Per-scene background — Phase 2.D. Used by HeyGen video gen +
                  (future) ffmpeg behind-clip composition. */}
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`timeline-bg-${e.scene_id}`}>
                <span title="Background colour" className="text-[10px]">BG</span>
                <input
                  type="color"
                  value={(e.background?.value && /^#[0-9a-fA-F]{6}$/.test(e.background.value)) ? e.background.value : "#1a1a1a"}
                  onChange={(ev) => updateEdit(i, { background: { kind: "color", value: ev.target.value } })}
                  className="w-7 h-6 cursor-pointer border hairline"
                  data-testid={`timeline-bg-color-${e.scene_id}`}
                  title="Per-scene background colour (HeyGen)"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Voiceover — Phase 2.B */}
      <div className="card-flat p-3 flex items-center gap-3 flex-wrap" data-testid="timeline-voiceover">
        <SpeakerHigh size={16} weight="duotone" className="text-[hsl(var(--primary))]" />
        <div className="text-xs flex-1 min-w-0">
          <div className="font-medium inline-flex items-center gap-1.5">
            ElevenLabs voiceover
            {voStatus?.voiceover_enabled && voStatus?.scenes_with_voiceover > 0 && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#5FB37C]/15 text-[#5FB37C]">
                ON · {voStatus.scenes_with_voiceover}/{voStatus.scenes_total} scenes
              </span>
            )}
          </div>
          {voStatus?.elevenlabs_key_configured ? (
            <div className="text-[10px] text-muted-foreground">
              {voStatus?.voice_label
                ? <>Voice: <b>{voStatus.voice_label}</b> · uses the first cast member's <code className="text-[9px]">voice_id</code> (or ElevenLabs default if cast has none)</>
                : <>Uses the first cast member's <code className="text-[9px]">voice_id</code>, or the ElevenLabs default voice if none configured</>}
            </div>
          ) : (
            <div className="text-[10px] text-amber-600">
              No ElevenLabs key configured. Paste one under Storyboard → Provider keys to enable.
            </div>
          )}
        </div>
        <button
          onClick={generateVoiceover}
          disabled={busy || !voStatus?.elevenlabs_key_configured}
          className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="timeline-voiceover-generate"
        >
          <Sparkle size={11} weight="duotone" />
          {voStatus?.scenes_with_voiceover > 0 ? "Regenerate" : "Generate voiceover"}
        </button>
        {voStatus?.voiceover_enabled && (
          <button
            onClick={clearVoiceover}
            className="btn-ghost px-2 py-1.5 text-[11px] text-muted-foreground"
            data-testid="timeline-voiceover-clear"
          >
            <X size={11} weight="bold" /> Disable
          </button>
        )}
      </div>

      {/* Music */}
      <div className="card-flat p-3 flex items-center gap-3 flex-wrap" data-testid="timeline-music">
        <MusicNote size={16} weight="duotone" className="text-[hsl(var(--secondary))]" />
        <div className="text-xs">
          <div className="font-medium">Background music</div>
          <div className="text-[10px] text-muted-foreground">Mixed under the timeline at the volume below (10 MB max)</div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          onChange={(e) => uploadMusic(e.target.files?.[0])}
          className="hidden"
          data-testid="timeline-music-file"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1"
          data-testid="timeline-music-upload"
        >
          <Upload size={12} weight="duotone" /> {music.file_id ? "Replace" : "Upload"}
        </button>
        {music.file_id && (
          <span className="text-[10px] text-[#5FB37C]">
            <Check size={10} weight="bold" className="inline" /> uploaded
          </span>
        )}
        <label className="text-[11px] text-muted-foreground inline-flex items-center gap-2 ml-auto">
          Volume
          <input
            type="range" min={0} max={1} step={0.05}
            value={music.volume}
            onChange={(e) => setMusic((m) => ({ ...m, volume: parseFloat(e.target.value) }))}
            className="w-32"
            data-testid="timeline-music-volume"
          />
          <span className="w-8 text-right">{Math.round(music.volume * 100)}%</span>
        </label>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
        <div className="text-[11px] text-muted-foreground">
          {includedCount} of {edits.length} scenes will render.
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={saveEdits}
            disabled={busy}
            className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="timeline-save"
          >
            <FloppyDisk size={12} weight="duotone" /> Save edits
          </button>
          <button
            onClick={renderFinal}
            disabled={busy || includedCount === 0}
            className="btn-primary px-4 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="timeline-render"
          >
            <Sparkle size={12} weight="duotone" /> {busy ? "Working…" : "Render final"}
          </button>
        </div>
      </div>
    </div>
  );
}
